import {
  WSActions,
  WSElement,
  WSFlexBox,
  WSInfiniteScroll,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { BulkReviewProps } from "../../types";
import { Row } from "../Row";
import styles from "./index.module.scss";

export const BulkReviewSidebar: React.FC<BulkReviewProps<any>> = props => {
  const activeElementRef = useRef<HTMLDivElement>(null);

  const activeIndex = useMemo(() => {
    return props.rows.findIndex(row => row.id === props.activeId);
  }, [props.activeId, props.rows]);

  const isPrevActive = useMemo(() => {
    return activeIndex > 0;
  }, [activeIndex]);

  const isNextActive = useMemo(() => {
    return activeIndex < props.rows.length - 1;
  }, [activeIndex, props.rows]);

  const onPrev = useCallback(() => {
    props.onRowClick(props.rows[activeIndex - 1]);
  }, [props, activeIndex]);

  const onNext = useCallback(() => {
    props.onRowClick(props.rows[activeIndex + 1]);
  }, [props, activeIndex]);

  useEffect(() => {
    if (activeIndex === props.rows.length - 2 && props.hasNextPage) {
      props.fetchNextPage?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, props.hasNextPage]);

  useEffect(() => {
    if (activeElementRef.current) {
      activeElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [props.activeId]);

  return (
    <WSElement className={styles.bulkReview}>
      <WSElement className={styles.header}>
        <WSSectionToolbar
          title="Filtered view"
          button={
            !!props.onClose
              ? {
                  label: "Close",
                  rightIcon: "exit",
                  onClick: props.onClose
                }
              : undefined
          }
        />

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Prev",
              icon: "caret-left",
              kind: "Secondary",
              disabled: !isPrevActive,
              onClick: onPrev
            },
            {
              label: "Next",
              rightIcon: "caret-right",
              kind: "Secondary",
              disabled: !isNextActive,
              onClick: onNext
            }
          ]}
        />
      </WSElement>

      <WSElement className={styles.list}>
        {props.listSize && props.listSize > 5 && (
          <WSFlexBox.Center className={styles.statsContainer} px="L" py="M">
            <WSText.ParagraphXs color="gray500">
              Showing {props.listSize} items
            </WSText.ParagraphXs>
          </WSFlexBox.Center>
        )}
        <WSInfiniteScroll
          onLoad={() => {
            props.fetchNextPage?.();
          }}
          loadMore={!!props.fetchNextPage && props.hasNextPage}
        >
          {props.rows.map(row => (
            <WSElement
              className={classNames(styles.row, {
                [styles.active]: row.id === props.activeId
              })}
              key={row.id}
              onClick={() => props.onRowClick(row)}
              ref={row.id === props.activeId ? activeElementRef : null}
            >
              <Row {...row} />
            </WSElement>
          ))}
        </WSInfiniteScroll>
      </WSElement>
    </WSElement>
  );
};
